import { useMemo, useState } from 'react'
import {
  BarChart3,
  CalendarCheck2,
  CalendarDays,
  CalendarOff,
  CalendarX2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Grid3x3,
  Plus,
  Table2,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { CapacityDay, ResourceProfile, ResourceWorkType, ShiftDefinition } from '@/api/resourceCenterApi'
import { AbsenceDialog } from './AbsenceDialog'
import { SupportActivityDialog } from './SupportActivityDialog'
import { useResourceCapacity, useShiftDefinitions } from '@/hooks/useResourceCenter'
import { WORK_TYPE_CHIPS } from '@/lib/assignmentGroups'
import {
  cellView,
  dayHeader,
  hoursLabel,
  todayIso,
  weekdayOf,
  windowDates,
  windowLabel,
  windowStart,
  type CellSegment,
  type CellView,
} from '@/lib/resourceCalendar'
import { ABSENCE_STRIPES, SectionCard, Segmented, WORK_COLOR, longDate } from './shared'
import { CapacityChart, CapacityHeatmap, CapacityTable } from './CapacityViews'
import { shiftTone } from '../shiftTones'

type Mode = '2w' | '4w' | 'month'
/** Four ways to read the same window. */
type View = 'calendar' | 'chart' | 'heatmap' | 'table'

const VIEWS: { value: View; label: string; icon: LucideIcon }[] = [
  { value: 'calendar', label: 'Calendar', icon: CalendarDays },
  { value: 'chart', label: 'Chart', icon: BarChart3 },
  { value: 'heatmap', label: 'Heatmap', icon: Grid3x3 },
  { value: 'table', label: 'Table', icon: Table2 },
]
const SPAN: Record<'2w' | '4w', number> = { '2w': 14, '4w': 28 }

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}
function firstOfMonth(iso: string): string {
  const [y, m] = iso.split('-')
  return `${y}-${m}-01`
}
function addMonths(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const total = m - 1 + n
  const ny = y + Math.floor(total / 12)
  const nm = (((total % 12) + 12) % 12) + 1
  return `${ny}-${String(nm).padStart(2, '0')}-${String(Math.min(d, daysInMonth(ny, nm))).padStart(2, '0')}`
}
function monthLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}
function segmentLabel(type: ResourceWorkType | 'FLEX'): string {
  return type === 'FLEX' ? 'Dynamic' : WORK_TYPE_CHIPS[type].label
}

const LEGEND: { label: string; swatch: string; stripes?: boolean }[] = [
  { label: 'Process', swatch: WORK_COLOR.PROCESS },
  { label: 'Review', swatch: WORK_COLOR.REVIEW },
  { label: 'QA', swatch: WORK_COLOR.QA },
  { label: 'Support', swatch: WORK_COLOR.SUPPORT },
  { label: 'Dynamic', swatch: WORK_COLOR.FLEX },
  { label: 'Absence', swatch: 'bg-background', stripes: true },
  { label: 'Off / not started', swatch: 'bg-muted' },
]

/**
 * Daily capacity (slide 14, Overview tab): what is left for Process, Review,
 * QA and Support, day by day — a 2-week / 4-week / month window, a legend,
 * period totals, and a click-through breakdown per day. With `canManage`, each
 * day's + adds an unavailable event (vacation, sick…) or a support activity
 * starting that day, in the same dialogs the Time away tab uses.
 */
export function CapacityCalendar({
  profile,
  canManage = false,
}: {
  profile: ResourceProfile
  canManage?: boolean
}) {
  const userId = profile.userId
  const today = todayIso()
  // The day whose + was used, and what to add on it.
  const [adding, setAdding] = useState<{ date: string; kind: 'absence' | 'support' } | null>(null)
  const [mode, setMode] = useState<Mode>('2w')
  const [view, setView] = useState<View>('calendar')
  const [weekOffset, setWeekOffset] = useState(0)
  const [monthOffset, setMonthOffset] = useState(0)

  const changeMode = (m: Mode) => {
    setMode(m)
    setWeekOffset(0)
    setMonthOffset(0)
  }

  const monthAnchor = addMonths(firstOfMonth(today), monthOffset)
  const [monthY, monthM] = monthAnchor.split('-').map(Number)
  const monthDayCount = daysInMonth(monthY, monthM)

  const start = mode === 'month' ? monthAnchor : windowStart(today, weekOffset)
  const dates = mode === 'month' ? windowDates(start, monthDayCount) : windowDates(start, SPAN[mode])
  const end = dates[dates.length - 1]

  const cap = useResourceCapacity(start, end, userId)
  const shifts = useShiftDefinitions()
  const shiftByCode = useMemo(() => new Map((shifts.data ?? []).map(s => [s.code, s])), [shifts.data])
  // The center's whole list, in order: the same one the People schedule colours by.
  const shiftIds = useMemo(() => (shifts.data ?? []).map(s => s.id), [shifts.data])
  const rows = cap.data?.[0]?.days ?? []
  const byDate = useMemo(() => new Map(rows.map(d => [d.date, d])), [rows])

  // Month view pads to full weeks so it reads as a calendar grid.
  const leading = mode === 'month' ? weekdayOf(dates[0]) : 0
  const trailing = mode === 'month' ? 6 - weekdayOf(dates[dates.length - 1]) : 0
  const gridCells: (string | null)[] = [...Array(leading).fill(null), ...dates, ...Array(trailing).fill(null)]

  const totalAvailable = rows.reduce((n, d) => n + Math.max(0, d.shiftHrs - d.uaHrs), 0)
  const daysOn = rows.filter(d => d.state === 'ON').length
  const daysOff = rows.filter(d => d.state === 'OFF').length
  const absenceDays = rows.filter(d => d.uaHrs > 0 || d.state === 'UNAVAILABLE').length

  const rangeLabel = mode === 'month' ? monthLabel(monthAnchor) : windowLabel(start, SPAN[mode])
  const atToday = mode === 'month' ? monthOffset === 0 : weekOffset === 0
  // weekOffset counts weeks (windowStart adds weekOffset × 7 days).
  const step = mode === 'month' ? 1 : SPAN[mode] / 7
  const goPrev = () => (mode === 'month' ? setMonthOffset(monthOffset - 1) : setWeekOffset(weekOffset - step))
  const goNext = () => (mode === 'month' ? setMonthOffset(monthOffset + 1) : setWeekOffset(weekOffset + step))
  const goToday = () => (mode === 'month' ? setMonthOffset(0) : setWeekOffset(0))

  return (
    <SectionCard
      icon={BarChart3}
      title='Daily capacity'
      hint='shift − absences − support → PR / RV / QA'
      action={
        <div className='flex flex-wrap items-center gap-2'>
          <Segmented
            ariaLabel='Calendar window'
            value={mode}
            onChange={changeMode}
            options={[
              { value: '2w', label: '2 weeks' },
              { value: '4w', label: '4 weeks' },
              { value: 'month', label: 'Month' },
            ]}
          />
          <div className='flex items-center gap-1'>
            <Button size='icon' variant='ghost' className='h-8 w-8' aria-label='Previous period' onClick={goPrev}>
              <ChevronLeft className='h-4 w-4' />
            </Button>
            <span className='w-32 text-center text-sm font-semibold tabular-nums'>{rangeLabel}</span>
            <Button size='icon' variant='ghost' className='h-8 w-8' aria-label='Next period' onClick={goNext}>
              <ChevronRight className='h-4 w-4' />
            </Button>
            <Button size='sm' variant='outline' className='h-8' disabled={atToday} onClick={goToday}>
              Today
            </Button>
          </div>
        </div>
      }
    >
      {cap.error && <p className='text-xs text-destructive'>{cap.error.message}</p>}

      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div role='tablist' aria-label='Capacity view' className='inline-flex rounded-lg bg-muted p-1'>
        {VIEWS.map(v => (
          <button
            key={v.value}
            type='button'
            role='tab'
            aria-selected={view === v.value}
            onClick={() => setView(v.value)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
              view === v.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <v.icon className='h-3.5 w-3.5' /> {v.label}
          </button>
        ))}
        </div>
        {/* The period at a glance, in one line rather than four cards. */}
        {rows.length > 0 && (
          <div className='flex flex-wrap items-center gap-1.5 text-xs'>
            <SummaryChip icon={Clock} value={`${hoursLabel(totalAvailable)} h`} label='available' strong />
            <SummaryChip icon={CalendarCheck2} value={daysOn} label='on' />
            <SummaryChip icon={CalendarX2} value={daysOff} label='off' />
            <SummaryChip icon={CalendarOff} value={absenceDays} label='absent' />
          </div>
        )}
      </div>

      {cap.isLoading && <div className='h-40 animate-pulse rounded-lg bg-muted/50' />}
      {cap.isPlaceholderData && (
        <p className='text-xs text-muted-foreground'>Loading {rangeLabel}… showing the previous window until it arrives.</p>
      )}

      {view === 'calendar' && (
        <div className='grid grid-cols-7 gap-1.5'>
          {gridCells.map((date, i) =>
            date === null ? (
              <div key={`pad-${i}`} />
            ) : (
              <DayCell
                key={date}
                date={date}
                today={today}
                day={byDate.get(date)}
                shiftByCode={shiftByCode}
                shiftIds={shiftIds}
                onAdd={canManage ? kind => setAdding({ date, kind }) : undefined}
              />
            ),
          )}
        </div>
      )}
      {/* Mounted only while open, so each one starts on the day it was opened from. */}
      {adding?.kind === 'absence' && (
        <AbsenceDialog
          profile={profile}
          open
          initialDate={adding.date}
          onOpenChange={o => !o && setAdding(null)}
        />
      )}
      {adding?.kind === 'support' && (
        <SupportActivityDialog
          profile={profile}
          open
          initialDate={adding.date}
          onOpenChange={o => !o && setAdding(null)}
        />
      )}
      {view === 'chart' && rows.length > 0 && <CapacityChart days={rows} today={today} />}
      {view === 'heatmap' && (
        <CapacityHeatmap
          dates={dates}
          byDate={byDate}
          today={today}
          leading={weekdayOf(dates[0])}
          stripes={ABSENCE_STRIPES}
        />
      )}
      {view === 'table' && rows.length > 0 && <CapacityTable days={rows} today={today} />}

      <div
        className={cn(
          'flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t pt-3 text-xs text-muted-foreground',
          view !== 'calendar' && 'hidden',
        )}
      >
        {LEGEND.map(l => (
          <span key={l.label} className='flex items-center gap-1.5'>
            <span
              className={cn('h-2.5 w-4 rounded-sm border', l.swatch)}
              style={l.stripes ? { backgroundImage: ABSENCE_STRIPES } : undefined}
            />
            {l.label}
          </span>
        ))}
      </div>
    </SectionCard>
  )
}

function DayCell({
  date,
  today,
  day,
  shiftByCode,
  shiftIds,
  onAdd,
}: {
  date: string
  today: string
  day: CapacityDay | undefined
  shiftByCode: Map<string, ShiftDefinition>
  shiftIds: string[]
  /** Set when the viewer may add time away; shows the day's +. */
  onAdd?: (kind: 'absence' | 'support') => void
}) {
  const h = dayHeader(date)
  const isToday = date === today
  const view: CellView = day ? cellView(day) : { kind: 'not-started' }
  const shift = day && day.shiftCode ? shiftByCode.get(day.shiftCode) : undefined

  const big =
    view.kind === 'on' ? hoursLabel(view.hours) : view.kind === 'off' ? 'OFF' : view.kind === 'absent' ? '0' : '—'

  const cell = (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={`${longDate(date)}: ${view.kind === 'on' ? `${hoursLabel(view.hours)} h available` : view.kind}`}
          className={cn(
            'flex h-32 w-full flex-col justify-between rounded-xl border p-2 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            h.weekend && 'bg-muted/30',
            view.kind === 'off' && 'bg-muted/50',
            view.kind === 'not-started' && 'border-dashed text-muted-foreground/50',
            view.kind === 'absent' && 'bg-rose-50/60',
            isToday && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
          )}
        >
          <div className='flex items-start justify-between gap-1'>
            <span className={cn('text-[10px] font-medium leading-tight', isToday ? 'text-primary' : 'text-muted-foreground')}>
              {h.dow}
              <br />
              {h.md}
            </span>
            {day?.shiftCode && (
              <span
                className={cn(
                  'rounded-md px-1.5 py-0.5 font-mono text-[10px] font-bold',
                  shift ? shiftTone(shiftIds, shift.id).cell : 'bg-muted text-muted-foreground',
                )}
              >
                {day.shiftCode}
              </span>
            )}
          </div>
          <div className='flex flex-1 flex-col items-center justify-center'>
            <span
              className={cn(
                'text-2xl font-semibold tabular-nums tracking-tight',
                view.kind === 'not-started' && 'text-muted-foreground/50',
                view.kind === 'off' && 'text-xs font-medium text-muted-foreground',
                view.kind === 'absent' && 'text-rose-700',
              )}
            >
              {big}
            </span>
            {/* When they are on site, right on the day. */}
            {shift && (view.kind === 'on' || view.kind === 'absent') && (
              <span className='mt-0.5 flex items-center gap-1 text-[11px] font-medium tabular-nums text-muted-foreground'>
                <Clock className='h-3 w-3' />
                {shift.startTime}–{shift.endTime}
              </span>
            )}
          </div>
          {view.kind === 'on' && (
            <div className='flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted'>
              {view.segments.map(s => (
                <span
                  key={s.type}
                  className={WORK_COLOR[s.type]}
                  style={{ width: `${day ? (s.hrs / (day.shiftHrs || 1)) * 100 : 0}%` }}
                />
              ))}
              {view.uaHrs > 0 && (
                <span
                  style={{ width: `${day ? (view.uaHrs / (day.shiftHrs || 1)) * 100 : 0}%`, backgroundImage: ABSENCE_STRIPES }}
                />
              )}
            </div>
          )}
          {view.kind === 'absent' && <div className='h-1.5 w-full rounded-full' style={{ backgroundImage: ABSENCE_STRIPES }} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 text-sm' align='center'>
        <p className='font-semibold'>{longDate(date)}</p>
        <DayBreakdown view={view} day={day} shift={shift} />
      </PopoverContent>
    </Popover>
  )

  if (!onAdd) return cell
  // The + sits beside the day button, not inside it: a button cannot hold one.
  return (
    <div className='group/day relative'>
      {cell}
      <AddOnDay date={date} onAdd={onAdd} />
    </div>
  )
}

/** The day's +: add an unavailable event or a support activity on it. */
function AddOnDay({ date, onAdd }: { date: string; onAdd: (kind: 'absence' | 'support') => void }) {
  const [open, setOpen] = useState(false)
  const pick = (kind: 'absence' | 'support') => {
    setOpen(false)
    onAdd(kind)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={`Add time away on ${longDate(date)}`}
          title='Add an unavailable event or support activity'
          className={cn(
            // On the day's top corner, overlapping its edge, so it covers nothing inside.
            'absolute -right-1.5 -top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-primary/40 bg-background text-primary shadow-md transition-all',
            'opacity-0 hover:bg-primary hover:text-primary-foreground focus-visible:opacity-100 group-hover/day:opacity-100',
            open && 'opacity-100',
          )}
        >
          <Plus className='h-3.5 w-3.5' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-1.5' align='end'>
        <p className='px-2 pb-1 pt-1 text-xs font-medium text-muted-foreground'>Add on {longDate(date)}</p>
        <button
          type='button'
          onClick={() => pick('absence')}
          className='flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left hover:bg-accent'
        >
          <span
            className='mt-0.5 h-4 w-4 shrink-0 rounded border border-rose-300'
            style={{ backgroundImage: ABSENCE_STRIPES }}
          />
          <span>
            <span className='block text-sm font-medium'>Unavailable event</span>
            <span className='block text-xs text-muted-foreground'>Vacation, sick, training — a full day or hours</span>
          </span>
        </button>
        <button
          type='button'
          onClick={() => pick('support')}
          className='flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left hover:bg-accent'
        >
          <span className={cn('mt-0.5 h-4 w-4 shrink-0 rounded', WORK_COLOR.SUPPORT)} />
          <span>
            <span className='block text-sm font-medium'>Support activity</span>
            <span className='block text-xs text-muted-foreground'>Hours reserved before PR / RV work</span>
          </span>
        </button>
      </PopoverContent>
    </Popover>
  )
}

function DayBreakdown({
  view,
  day,
  shift,
}: {
  view: CellView
  day: CapacityDay | undefined
  shift: ShiftDefinition | undefined
}) {
  if (view.kind === 'not-started') return <p className='mt-1 text-xs text-muted-foreground'>No shift assigned yet.</p>
  if (view.kind === 'off') return <p className='mt-1 text-xs text-muted-foreground'>Day off — not part of the shift pattern.</p>
  if (view.kind === 'absent')
    return (
      <div className='mt-1.5 space-y-1 text-xs'>
        <Row label='Absence' value={view.code ?? 'Unavailable'} />
        {view.shiftCode && <Row label='Shift' value={shift ? `${shift.name} · ${shift.startTime}–${shift.endTime}` : view.shiftCode} />}
        <p className='pt-1 text-muted-foreground'>The whole day is unavailable.</p>
      </div>
    )
  // kind === 'on'
  const segments: CellSegment[] = view.segments
  return (
    <div className='mt-1.5 space-y-1 text-xs'>
      {shift && <Row label='Shift' value={`${shift.name} · ${shift.startTime}–${shift.endTime}`} />}
      <Row label='Net available' value={`${hoursLabel(view.hours)} h`} bold />
      {view.uaHrs > 0 && <Row label={`Absence${view.uaCode ? ` (${view.uaCode})` : ''}`} value={`${hoursLabel(view.uaHrs)} h`} />}
      {segments.length > 0 && (
        <div className='space-y-0.5 border-t pt-1'>
          {segments.map(s => (
            <Row key={s.type} label={segmentLabel(s.type)} value={`${hoursLabel(s.hrs)} h`} />
          ))}
        </div>
      )}
      {day && day.shiftHrs > 0 && segments.length === 0 && view.uaHrs === 0 && (
        <p className='pt-1 text-muted-foreground'>Nothing assigned yet.</p>
      )}
    </div>
  )
}

/** One fact about the period, as a compact pill. */
function SummaryChip({
  icon: Icon,
  value,
  label,
  strong,
}: {
  icon: LucideIcon
  value: string | number
  label: string
  strong?: boolean
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1',
        strong ? 'border-primary/30 bg-primary/5 text-primary' : 'bg-background text-muted-foreground',
      )}
    >
      <Icon className='h-3.5 w-3.5' />
      <span className={cn('font-semibold tabular-nums', !strong && 'text-foreground')}>{value}</span>
      {label}
    </span>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className='flex items-center justify-between gap-3'>
      <span className='text-muted-foreground'>{label}</span>
      <span className={cn('tabular-nums', bold && 'font-semibold')}>{value}</span>
    </div>
  )
}
